import React, { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserRole } from "./types";
import { useAuth } from "./hooks/useAuth";
import { LoadingFallback } from "./components/layout/LoadingFallback";
import { AppLayout } from "./components/layout/AppLayout";

// Lazy load all page components for code splitting
const AuthPage = lazy(() => import("./pages/Auth").then(module => ({ default: module.AuthPage })));
const Index = lazy(() => import("./pages/Index"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/AdminDashboard").then(module => ({ default: module.AdminDashboard })));
const AdminSpecialists = lazy(() => import("./pages/AdminSpecialists"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminActivityLogs = lazy(() => import("./pages/AdminActivityLogs"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminQualifications = lazy(() => import("./pages/AdminQualifications"));
const AdminPaymentReconciliation = lazy(() => import("./pages/AdminPaymentReconciliation"));
const AdminPPM = lazy(() => import("./pages/AdminPPM"));

// Specialist pages
const SpecialistDashboard = lazy(() => import("./pages/SpecialistDashboard").then(module => ({ default: module.SpecialistDashboard })));
const SpecialistProfile = lazy(() => import("./pages/SpecialistProfile").then(module => ({ default: module.SpecialistProfile })));
const SpecialistJobs = lazy(() => import("./pages/SpecialistJobs").then(module => ({ default: module.SpecialistJobs })));
const SpecialistEarnings = lazy(() => import("./pages/SpecialistEarnings").then(module => ({ default: module.SpecialistEarnings })));
const SpecialistInventory = lazy(() => import("./pages/SpecialistInventory").then(module => ({ default: module.SpecialistInventory })));
const SpecialistPPMJobs = lazy(() => import("./pages/SpecialistPPMJobs"));
const SpecialistPPMDetail = lazy(() => import("./pages/SpecialistPPMDetail"));

// Partner pages
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard").then(module => ({ default: module.PartnerDashboard })));
const PartnerAssets = lazy(() => import("./pages/PartnerAssets").then(module => ({ default: module.PartnerAssets })));
const AssetDetail = lazy(() => import("./pages/AssetDetail").then(module => ({ default: module.AssetDetail })));
const PartnerJobs = lazy(() => import("./pages/PartnerJobs").then(module => ({ default: module.PartnerJobs })));
const PartnerInvoices = lazy(() => import("./pages/PartnerInvoices").then(module => ({ default: module.PartnerInvoices })));
const PartnerSettings = lazy(() => import("./pages/PartnerSettings").then(module => ({ default: module.PartnerSettings })));
const PartnerPPM = lazy(() => import("./pages/PartnerPPM"));
const PartnerPPMDetail = lazy(() => import("./pages/PartnerPPMDetail"));
const PartnerPPMReport = lazy(() => import("./pages/PartnerPPMReport"));

// Shared pages
const JobDetails = lazy(() => import("./pages/JobDetails").then(module => ({ default: module.JobDetails })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy").then(module => ({ default: module.PrivacyPolicy })));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions").then(module => ({ default: module.TermsAndConditions })));

// Onboarding pages
const SpecialistOnboarding = lazy(() => import("./pages/onboarding/specialist/SpecialistOnboarding"));
const PartnerOnboarding = lazy(() => import("./pages/onboarding/partner/PartnerOnboarding"));

const queryClient = new QueryClient();

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requiredRole?: UserRole;
}> = ({ children, requiredRole }) => {
  const { isAuthenticated, userRole, loading } = useAuth();

  console.log("ProtectedRoute:", { isAuthenticated, userRole, loading, requiredRole });

  if (loading) {
    console.log("ProtectedRoute: Loading...");
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-2">Checking permissions...</div>
          <div className="text-sm text-muted-foreground">Please wait</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log("ProtectedRoute: Not authenticated, redirecting to /auth");
    return <Navigate to="/auth" replace />;
  }

  // If user is authenticated but has no role (no profile), redirect to auth for profile setup
  if (!userRole) {
    console.log("ProtectedRoute: No role found, redirecting to /auth");
    return <Navigate to="/auth" replace />;
  }

  if (requiredRole && userRole !== requiredRole) {
    console.log(`ProtectedRoute: Role mismatch (${userRole} !== ${requiredRole}), redirecting to /${userRole}`);
    // If user has wrong role, redirect to their correct dashboard
    return <Navigate to={`/${userRole}`} replace />;
  }

  console.log("ProtectedRoute: Access granted");
  return <>{children}</>;
};

const App = () => {
  const { isAuthenticated, loading, userRole } = useAuth();

  console.log("App: Render state:", { isAuthenticated, loading, userRole });

  if (loading) {
    console.log("App: Showing loading screen");
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <div className="text-lg">Loading your account...</div>
              <div className="text-sm text-muted-foreground">
                Auth: {isAuthenticated ? '✓' : '○'} | Role: {userRole || '○'}
              </div>
            </div>
          </div>
          <SonnerToaster />
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  console.log("App: Rendering main app, authenticated:", isAuthenticated, "userRole:", userRole);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <SonnerToaster />
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/privacy-policy" element={
              <Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicy />
              </Suspense>
            } />
            <Route path="/terms" element={
              <Suspense fallback={<LoadingFallback />}>
                <TermsAndConditions />
              </Suspense>
            } />
            
            {/* Auth Route */}
            <Route path="/auth" element={
              <Suspense fallback={<LoadingFallback />}>
                {(() => {
                  // Check for password recovery flow - allow access even if authenticated
                  // Check BOTH hash and query parameters for recovery tokens
                  const hashParams = new URLSearchParams(window.location.hash.substring(1));
                  const queryParams = new URLSearchParams(window.location.search);
                  const isRecoveryHash = hashParams.get('type') === 'recovery';
                  const isRecoveryQuery = queryParams.get('type') === 'recovery';
                  const resetFlag = queryParams.get('reset') === 'true'; // Check custom reset flag
                  const isRecoveryFlow = sessionStorage.getItem('password_recovery_flow') === 'true';
                  
                  // If in recovery flow, always show AuthPage
                  if (isRecoveryHash || isRecoveryQuery || resetFlag || isRecoveryFlow) {
                    return <AuthPage />;
                  }
                  
                  // Otherwise, redirect authenticated users to their dashboard
                  return isAuthenticated && userRole ? 
                    <Navigate to={`/${userRole}`} replace /> : 
                    <AuthPage />;
                })()}
              </Suspense>
            } />
            
            {/* Root redirect - uses AuthRedirect to check onboarding status */}
            <Route path="/" element={
              <Suspense fallback={<LoadingFallback />}>
                <Index />
              </Suspense>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRole="admin">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/specialists" element={
              <ProtectedRoute requiredRole="admin">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminSpecialists />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requiredRole="admin">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminUsers />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/activity-logs" element={
              <ProtectedRoute requiredRole="admin">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminActivityLogs />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute requiredRole="admin">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminSettings />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/qualifications" element={
              <ProtectedRoute requiredRole="admin">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminQualifications />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/reconciliation" element={
              <ProtectedRoute requiredRole="admin">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminPaymentReconciliation />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/ppm" element={
              <ProtectedRoute requiredRole="admin">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminPPM />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Specialist Routes */}
            <Route path="/specialist" element={
              <ProtectedRoute requiredRole="specialist">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SpecialistDashboard />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/specialist/profile" element={
              <ProtectedRoute requiredRole="specialist">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SpecialistProfile />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/specialist/jobs" element={
              <ProtectedRoute requiredRole="specialist">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SpecialistJobs />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/specialist/earnings" element={
              <ProtectedRoute requiredRole="specialist">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SpecialistEarnings />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/specialist/inventory" element={
              <ProtectedRoute requiredRole="specialist">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SpecialistInventory />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/specialist/jobs/:jobId" element={
              <ProtectedRoute requiredRole="specialist">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <JobDetails />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/specialist/ppm" element={
              <ProtectedRoute requiredRole="specialist">
                <Suspense fallback={<LoadingFallback />}>
                  <SpecialistPPMJobs />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/specialist/ppm/:id" element={
              <ProtectedRoute requiredRole="specialist">
                <Suspense fallback={<LoadingFallback />}>
                  <SpecialistPPMDetail />
                </Suspense>
              </ProtectedRoute>
            } />
            
            {/* Partner Routes */}
            <Route path="/partner" element={
              <ProtectedRoute requiredRole="partner">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PartnerDashboard />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/partner/assets" element={
              <ProtectedRoute requiredRole="partner">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PartnerAssets />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/partner/assets/:assetId" element={
              <ProtectedRoute requiredRole="partner">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AssetDetail />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/partner/jobs" element={
              <ProtectedRoute requiredRole="partner">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PartnerJobs />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/partner/jobs/:jobId" element={
              <ProtectedRoute requiredRole="partner">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <JobDetails />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/partner/invoices" element={
              <ProtectedRoute requiredRole="partner">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PartnerInvoices />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/partner/settings" element={
              <ProtectedRoute requiredRole="partner">
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PartnerSettings />
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/partner/ppm" element={
              <ProtectedRoute requiredRole="partner">
                <Suspense fallback={<LoadingFallback />}>
                  <PartnerPPM />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/partner/ppm/:id" element={
              <ProtectedRoute requiredRole="partner">
                <Suspense fallback={<LoadingFallback />}>
                  <PartnerPPMDetail />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/partner/ppm/report/:id" element={
              <ProtectedRoute requiredRole="partner">
                <Suspense fallback={<LoadingFallback />}>
                  <PartnerPPMReport />
                </Suspense>
              </ProtectedRoute>
            } />
            
            <Route path="/privacy" element={
              <Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicy />
              </Suspense>
            } />
            <Route path="/terms" element={
              <Suspense fallback={<LoadingFallback />}>
                <TermsAndConditions />
              </Suspense>
            } />
            
            <Route path="/onboarding/specialist" element={
              <ProtectedRoute requiredRole="specialist">
                <Suspense fallback={<LoadingFallback />}>
                  <SpecialistOnboarding />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/onboarding/partner" element={
              <ProtectedRoute requiredRole="partner">
                <Suspense fallback={<LoadingFallback />}>
                  <PartnerOnboarding />
                </Suspense>
              </ProtectedRoute>
            } />
            
            <Route path="*" element={<Navigate to="/auth" replace />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;